import { ValidatorOrShape } from '../validators/types'
import { ValidationResult } from '../public'
import { AssertShape, createAssertShape } from './assertShape'
import { IsObjectOfShape, createIsObjectOfShape } from './isObjectOfShape'
import { ShapeFor } from './shapeFor'
import { ValidateShapeOptions, validateShape } from './validateShape'

export interface ValidateSpecificShapeFunction {
  (
    obj: unknown,
    options?: ValidateShapeOptions
  ): { success: boolean, results: ValidationResult[] }
}


/**
 * A collection of functions for validating and asserting the shape of an object.
 * 
 * @template T - The type of object being validated
 */
type VKit<T extends object> = {
  /** Function that asserts an object matches the shape */
  assertShape: AssertShape<T>
  /** Function that checks if an object matches the shape */
  isObjectOfShape: IsObjectOfShape<T>
  /** Function that validates an object against a shape */
  validateShape: ValidateSpecificShapeFunction
  /** The shape definition for the type */
  shape: ShapeFor<T>
}

/**
 * Creates a kit for validating and asserting the shape of an object.
 * 
 * @template T - The type of object being validated
 * @param shape - The shape to validate against
 * @returns A kit containing validation and assertion functions
 */
const createKit = <T extends object>(shape: ShapeFor<T>): VKit<T> => {
  return {
    assertShape: createAssertShape<T>(shape),
    isObjectOfShape: createIsObjectOfShape<T>(shape),
    validateShape: (obj, options) => validateShape(obj, shape as ValidatorOrShape, options),
    shape
  }
}

export { createKit, VKit }
